import { TimeSeriesIndicatorIcon } from 'features/knowledgeDocumentViewer/components/viewer/shared/TimeSeriesIndicatorIcon'
import { useDocumentTabDataContext } from 'features/knowledgeDocumentViewer/contexts/DocumentTabDataContext'
import { useInstrumentSelectionOptional } from 'features/knowledgeDocumentViewer/contexts/InstrumentSelectionContext'
import { useTimeSeriesLinkContext } from 'features/knowledgeDocumentViewer/contexts/TimeSeriesLinkContext'
import type { AliasHighlight } from 'features/knowledgeDocumentViewer/types/documentSubjects'
import type { NormalizedBBox } from 'features/knowledgeDocumentViewer/types/ocrViewer'
import { matchSubjectDatapoints } from 'features/knowledgeDocumentViewer/utils/instrumentTagUtils'
import React, { useMemo } from 'react'

type AliasHighlightOverlayProps = {
  imageNatural: { w: number; h: number }
  firstPageWidth?: number
  isMultipage?: boolean
  onAliasClick?: (highlight: AliasHighlight, event?: React.MouseEvent) => void
  onAliasHover?: (
    highlight: AliasHighlight | null,
    event?: React.MouseEvent
  ) => void
}

const FILL = 'rgba(168, 85, 247, 0.25)'
const STROKE = 'rgba(168, 85, 247, 0.9)'
const SELECTED_FILL = 'rgba(34, 197, 94, 0.3)'
const SELECTED_STROKE = 'rgba(34, 197, 94, 1)'
const PADDING = 2

function getPageLayout(
  isRightPage: boolean,
  imageNatural: { w: number; h: number },
  firstPageWidth: number | undefined,
  isMultipage: boolean | undefined
): { pageWidth: number; xOffset: number } {
  if (!isMultipage || !firstPageWidth) {
    return { pageWidth: imageNatural.w, xOffset: 0 }
  }
  if (isRightPage) {
    return {
      pageWidth: imageNatural.w - firstPageWidth,
      xOffset: firstPageWidth,
    }
  }
  return { pageWidth: firstPageWidth, xOffset: 0 }
}

function AliasRect({
  bbox,
  pageWidth,
  xOffset,
  imageHeight,
  selected,
  label,
  hasTimeSeries,
  onClick,
  onMouseEnter,
  onMouseLeave,
}: {
  bbox: NormalizedBBox
  pageWidth: number
  xOffset: number
  imageHeight: number
  selected: boolean
  label: string
  hasTimeSeries: boolean
  onClick: (event: React.MouseEvent) => void
  onMouseEnter: (event: React.MouseEvent) => void
  onMouseLeave: () => void
}) {
  const x = xOffset + bbox.left * pageWidth - PADDING
  const y = bbox.top * imageHeight - PADDING
  const width = bbox.width * pageWidth + PADDING * 2

  return (
    <>
      <rect
        data-alias-label={label}
        x={x}
        y={y}
        width={width}
        height={bbox.height * imageHeight + PADDING * 2}
        rx={2}
        ry={2}
        fill={selected ? SELECTED_FILL : FILL}
        stroke={selected ? SELECTED_STROKE : STROKE}
        strokeWidth={selected ? 2.5 : 1.5}
        style={{ cursor: 'pointer', pointerEvents: 'auto' }}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      />
      {hasTimeSeries && (
        <TimeSeriesIndicatorIcon x={x + width - 6} y={y - 10} size={14} />
      )}
    </>
  )
}

export function AliasHighlightOverlay({
  imageNatural,
  firstPageWidth,
  isMultipage,
  onAliasClick,
  onAliasHover,
}: AliasHighlightOverlayProps) {
  const { aliasHighlights, pageNumber, documentSubjects } =
    useDocumentTabDataContext()
  const instrumentSelection = useInstrumentSelectionOptional()
  const { getMatchedDatapoints } = useTimeSeriesLinkContext()

  const selectedSubjectUids = useMemo(() => {
    const uids = new Set<string>()
    for (const instrument of instrumentSelection?.selected.values() ?? []) {
      if (instrument.subjectUid) uids.add(instrument.subjectUid)
    }
    return uids
  }, [instrumentSelection?.selected])

  const subjectUidsWithTimeSeries = useMemo(() => {
    const uids = new Set<string>()
    for (const subject of documentSubjects) {
      if (matchSubjectDatapoints(subject, getMatchedDatapoints).length > 0) {
        uids.add(subject.subject_uid)
      }
    }
    return uids
  }, [documentSubjects, getMatchedDatapoints])

  const visibleHighlights = aliasHighlights.filter(
    (highlight) =>
      highlight.page === pageNumber ||
      (isMultipage && highlight.page === pageNumber + 1)
  )

  if (visibleHighlights.length === 0) return null

  return (
    <g>
      {visibleHighlights.map((highlight, idx) => {
        const isRightPage = isMultipage === true && highlight.page > pageNumber
        const { pageWidth, xOffset } = getPageLayout(
          isRightPage,
          imageNatural,
          firstPageWidth,
          isMultipage
        )

        return (
          <AliasRect
            key={`${highlight.subjectUid}-${highlight.aliasNorm}-${idx}`}
            bbox={highlight.bbox}
            pageWidth={pageWidth}
            xOffset={xOffset}
            imageHeight={imageNatural.h}
            selected={selectedSubjectUids.has(highlight.subjectUid)}
            label={highlight.label}
            hasTimeSeries={subjectUidsWithTimeSeries.has(highlight.subjectUid)}
            onClick={(event) => onAliasClick?.(highlight, event)}
            onMouseEnter={(event) => onAliasHover?.(highlight, event)}
            onMouseLeave={() => onAliasHover?.(null)}
          />
        )
      })}
    </g>
  )
}
